import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { ArrowLeft, Loader2, Play } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { RawanPlayer } from "@/components/rawan/RawanPlayer";
import { decryptUrl } from "@/lib/safelink-crypto";
import { logSafelink } from "@/lib/safelink-log";
import { useAuth } from "@/lib/auth";
import { getItemBySlug, useAllItems } from "@/lib/library-store";
import { getSeasonsForSeries } from "@/lib/content/loader";
import { getEpisodesForSeries } from "@/lib/episodes-store";
import { useTheme } from "@/lib/theme-context";
import { useDeviceClass } from "@/hooks/use-device";
import { consumeInternalNav } from "@/lib/internal-nav";
import { pickTitle } from "@/lib/i18n";
import { localizeEpisodeTitle } from "@/lib/episode-title";


const search = z.object({
  t: z.string().optional(),                  // OUTER token; plaintext = INNER ciphertext
  slug: z.string().optional(),
  kind: z.enum(["movie", "series"]).optional(),
  ep: z.string().optional(),                 // e.g. "s1e1"
  q: z.string().optional(),                  // optional quality label
});

export const Route = createFileRoute("/play")({
  validateSearch: search,
  head: () => ({
    meta: [
      { title: "مشغل Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PlayPage,
});

function PlayPage() {
  const { t: token, slug, kind = "movie", ep, q } = Route.useSearch();
  const { language } = useTheme();
  const tr = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const navigate = useNavigate();

  // Token حيث plaintext هو inner-ciphertext (طبقة 2). نفك الطبقة 1 هنا.
  const [innerToken, setInnerToken] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { email } = useAuth();

  // مشغّل RawanUI مخصّص للهاتف والتابلت فقط. على أجهزة الحاسوب الحقيقية
  // (ماوس + بدون شاشة لمس) نُعيد المستخدم إلى صفحة المحتوى بصرف النظر عن
  // حجم النافذة.
  const device = useDeviceClass();
  useEffect(() => {
    if (!device.ready || !slug) return;
    const target = { to: kind === "series" ? "/series/$slug" as const : "/movie/$slug" as const, params: { slug } };
    if (device.isDesktop) { navigate(target); return; }
    // حارس المشاركة: المشغّل يفتح فقط إذا جاء المستخدم من صفحة المحتوى داخل نفس الجلسة.
    const path = `/play?slug=${slug}&kind=${kind}${ep ? `&ep=${ep}` : ""}`;
    if (!consumeInternalNav(path)) navigate(target);
  }, [device.ready, device.isDesktop, kind, slug, ep, navigate]);


  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setErr(tr("لا يوجد مصدر صالح للتشغيل. تأكد من صحة الرابط.", "No valid source. Check the link.", "Aucune source valide. Vérifiez le lien."));
      logSafelink({ source: "play", status: "invalid", kind, slug, userId: email ?? null, note: "missing token" });
      return;
    }
    decryptUrl(token)
      .then((inner) => {
        if (!cancelled) {
          setInnerToken(inner);
          logSafelink({ source: "play", status: "success", kind, slug, userId: email ?? null });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErr(tr("تعذّر فك تشفير الرابط — قد يكون منتهي الصلاحية أو تالفاً.", "Could not decrypt link — it may be expired or invalid.", "Impossible de déchiffrer le lien — il a peut-être expiré ou il est invalide."));
          logSafelink({ source: "play", status: "decrypt-failed", kind, slug, userId: email ?? null });
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const allItems = useAllItems();
  const item = slug ? getItemBySlug(slug) ?? allItems.find((m) => m.slug === slug) : undefined;

  // قائمة الحلقات للمسلسلات
  const episodes = useMemo(() => {
    if (!slug || kind !== "series") return [];
    const ls = getEpisodesForSeries(slug);
    const fromContent = getSeasonsForSeries(slug).flatMap((season) =>
      season.episodes.map((e) => ({
        id: `${slug}-s${season.season}e${e.episode_number}`,
        season: season.season,
        number: e.episode_number,
        title: localizeEpisodeTitle(e.title, language),
        url: e.watch_url || "",
        thumbnail: e.thumbnail,
      })),
    );
    const seen = new Set<string>();
    return [...fromContent, ...ls.map((e) => ({
      id: e.id, season: e.season, number: e.number, title: e.title, url: e.url, thumbnail: e.thumbnail,
    }))]
      .filter((e) => {
        const k = `s${e.season}e${e.number}`;
        if (seen.has(k)) return false;
        seen.add(k); return true;
      })
      .sort((a, b) => a.season - b.season || a.number - b.number);
  }, [slug, kind]);

  const currentEp = ep?.toLowerCase();

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-6 max-w-5xl">
        {slug && (
          <Link
            to={kind === "series" ? "/series/$slug" : "/movie/$slug"}
            params={{ slug }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className={`h-4 w-4 ${(language === "ar" ? "rotate-180" : "")}`} />
            {item ? pickTitle(item, language) : tr("\u0631\u062c\u0648\u0639", "Back", "Retour")}
          </Link>
        )}

        <h1 className="text-xl sm:text-2xl font-extrabold mb-4">
          {item ? pickTitle(item, language) : tr("\u0645\u0634\u063a\u0644 Rawan UI", "Rawan UI Player", "Lecteur Rawan UI")}
          {currentEp && <span className="ms-2 text-primary">— {currentEp.toUpperCase()}</span>}
          {q && <span className="ms-2 text-xs px-2 py-1 rounded bg-primary/15 text-primary">{q}</span>}
        </h1>

        {err ? (
          <div className="aspect-video grid place-items-center rounded-2xl border border-destructive/30 bg-destructive/10 text-destructive">
            {err}
          </div>
        ) : innerToken ? (
          <RawanPlayer
            protectedUrl={innerToken}
            poster={item?.backdrop}
            title={item ? pickTitle(item, language) : undefined}
            kind={kind}
          />

        ) : (
          <div className="aspect-video grid place-items-center rounded-2xl border border-border bg-card">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="mt-3 text-sm text-muted-foreground">{tr("جارٍ التحقق من المصدر…", "Verifying source…", "Vérification de la source…")}</p>
            </div>
          </div>
        )}

        {/* الحلقات (للمسلسلات فقط) */}
        {kind === "series" && episodes.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-extrabold mb-3">{tr("\u0627\u0644\u062d\u0644\u0642\u0627\u062a", "Episodes", "\u00c9pisodes")}</h2>
            <div className="grid sm:grid-cols-2 gap-2">
              {episodes.map((e) => {
                const key = `s${e.season}e${e.number}`;
                const active = key === currentEp;
                return (
                  <button
                    key={e.id}
                    onClick={async () => {
                      if (!slug) return;
                      const { encryptDouble } = await import("@/lib/safelink-crypto");
                      const newToken = await encryptDouble(e.url || "", { kind: "series", slug, title: e.title, ttlSec: 6 * 60 * 60 });
                      const { allowNextInternalNav } = await import("@/lib/internal-nav");
                      allowNextInternalNav();
                      navigate({ to: "/play", search: { t: newToken, slug, kind: "series", ep: key } });

                    }}

                    className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 p-3 rounded-xl border transition text-start ${
                      active
                        ? "bg-primary/15 border-primary"
                        : "bg-card border-border hover:border-primary"
                    }`}
                  >
                    <span className="shrink-0 text-xs font-extrabold px-2 py-1 rounded text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
                      S{e.season}·E{e.number}
                    </span>
                    <span className="min-w-0 truncate font-bold">{e.title}</span>
                    <Play className="h-4 w-4 text-primary shrink-0" />
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
